import { useEffect } from 'react';

import { isValidTheme, THEME_UPDATE_EVENT, ThemeValue } from './constant';

/**
 * GitLab fork: Syncs theme from intra-frame CustomEvent dispatched by jwtAutoLogin.
 *
 * When the parent GitLab frame sends a JWT login message (via postMessage),
 * jwtAutoLogin.ts validates the origin, persists the theme to localStorage,
 * and dispatches a THEME_UPDATE CustomEvent. This hook picks up that event
 * and updates React state via the ThemeProvider's setTheme callback, which
 * also writes to localStorage — so we intentionally do NOT write to
 * localStorage here to avoid a redundant triple-write.
 */
export function useExternalThemeSync(
	setTheme: (newTheme: ThemeValue) => void,
): void {
	useEffect(() => {
		const handleThemeUpdate = (event: Event): void => {
			if (!(event instanceof CustomEvent)) {
				return;
			}

			const nextTheme: unknown = event.detail?.theme;
			if (!isValidTheme(nextTheme)) {
				return;
			}

			setTheme(nextTheme);
		};

		window.addEventListener(THEME_UPDATE_EVENT, handleThemeUpdate);
		return (): void => {
			window.removeEventListener(THEME_UPDATE_EVENT, handleThemeUpdate);
		};
	}, [setTheme]);
}
